import AsyncStorage from '@react-native-async-storage/async-storage'

// The server cannot tell a card never sorted from a card filed under « Privé »
// on purpose: what « Classer des profils » already sorted is remembered on
// this device, per account, so the pile does not come back whole at each visit.
const CLASSIFIED_PROFILES_KEY = 'classified_profiles'

const storageKey = (accountId: string) => `${CLASSIFIED_PROFILES_KEY}_${accountId}`

const readIds = async (accountId: string): Promise<string[]> => {
  try {
    const stored = JSON.parse((await AsyncStorage.getItem(storageKey(accountId))) ?? '[]')
    return Array.isArray(stored) ? stored.filter((id) => typeof id === 'string') : []
  } catch {
    return []
  }
}

// Cards are sorted faster than the storage answers: changes wait for one
// another, so two of them never start from the same list and lose an id.
let pending: Promise<unknown> = Promise.resolve()

const change = (accountId: string, update: (ids: string[]) => string[]): Promise<void> => {
  const done = pending.then(async () => {
    const ids = await readIds(accountId)
    await AsyncStorage.setItem(storageKey(accountId), JSON.stringify(update(ids))).catch(() => {})
  })
  pending = done
  return done
}

/** The profiles already sorted on this device by this account. */
export function loadClassifiedIds(accountId: string): Promise<string[]> {
  const loaded = pending.then(() => readIds(accountId))
  pending = loaded
  return loaded
}

export function addClassifiedId(accountId: string, id: string): Promise<void> {
  return change(accountId, (ids) => (ids.includes(id) ? ids : [...ids, id]))
}

/** Puts a profile back in the pile, e.g. when its save was refused. */
export function removeClassifiedId(accountId: string, id: string): Promise<void> {
  return change(accountId, (ids) => ids.filter((known) => known !== id))
}

/** Forgets everything: the whole pile is proposed again. */
export async function clearClassifiedIds(accountId: string): Promise<void> {
  const done = pending.then(() =>
    AsyncStorage.removeItem(storageKey(accountId)).catch(() => {}),
  )
  pending = done
  return done
}

/** Drops the profiles that no longer exist, so deleted cards do not pile up. */
export function pruneClassifiedIds(accountId: string, existingIds: string[]): Promise<void> {
  const existing = new Set(existingIds)
  return change(accountId, (ids) => ids.filter((id) => existing.has(id)))
}
